/**
 * PocketStickyNotes Component
 *
 * Container orchestrator for weekly panel sticky notes.
 * Renders Goals of the Week and Focus of the Week panels.
 *
 * Features:
 * - Portal rendering to escape layout constraints
 * - Expanded panel management
 * - Minimized pill dock
 * - Resizable panels (desktop)
 * - PostgreSQL persistence
 */

import React, { useCallback, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Portal } from '@shared/components/ui/Portal';
import { StickyNotePanel } from './StickyNotePanel';
import { MinimizedPillDock } from './MinimizedPillDock';
import { useWeeklyPanels } from '../../hooks/useWeeklyPanels';
import { useAppPanelStore } from '@shared/store/useAppPanelStore';
import { useMinimizedUrlPanelsStore } from '@shared/store/minimizedUrlPanelsStore';
import type { PanelType, PanelSize } from '../../types/weeklyPanels';
import { panelConfigs } from '../../types/weeklyPanels';

/**
 * Main container for Pocket Sticky Notes feature
 */
export const PocketStickyNotes: React.FC = () => {
  const {
    goalsContent,
    focusContent,
    goalsPanel,
    focusPanel,
    isLoading,
    saveContent,
    updatePosition,
    updateSize,
    toggleCollapsed,
    setCollapsed,
  } = useWeeklyPanels();

  // Track which panel is focused/selected (for z-index management)
  const [focusedPanel, setFocusedPanel] = useState<PanelType | null>(null);

  // Handlers for goals panel
  const handleGoalsContentChange = useCallback(
    (content: string) => saveContent('goals', content),
    [saveContent]
  );

  const handleGoalsPositionChange = useCallback(
    (position: { x: number; y: number }) => updatePosition('goals', position),
    [updatePosition]
  );

  const handleGoalsMinimize = useCallback(
    () => toggleCollapsed('goals'),
    [toggleCollapsed]
  );

  // Handlers for goals size
  const handleGoalsSizeChange = useCallback(
    (size: PanelSize) => updateSize('goals', size),
    [updateSize]
  );

  // Handlers for focus panel
  const handleFocusContentChange = useCallback(
    (content: string) => saveContent('focus', content),
    [saveContent]
  );

  const handleFocusPositionChange = useCallback(
    (position: { x: number; y: number }) => updatePosition('focus', position),
    [updatePosition]
  );

  const handleFocusSizeChange = useCallback(
    (size: PanelSize) => updateSize('focus', size),
    [updateSize]
  );

  const handleFocusMinimize = useCallback(
    () => toggleCollapsed('focus'),
    [toggleCollapsed]
  );

  // Handler for restoring panels from pills
  const handleRestore = useCallback(
    (panelType: PanelType) => {
      setCollapsed(panelType, false);
    },
    [setCollapsed]
  );

  // Get list of collapsed panels for pill dock
  const collapsedPanels = useMemo(() => {
    const panels: Array<{ type: PanelType; config: typeof panelConfigs.goals }> = [];
    if (goalsPanel.isCollapsed) {
      panels.push({ type: 'goals', config: panelConfigs.goals });
    }
    if (focusPanel.isCollapsed) {
      panels.push({ type: 'focus', config: panelConfigs.focus });
    }
    return panels;
  }, [goalsPanel.isCollapsed, focusPanel.isCollapsed]);

  // Hide sticky notes when any panel is open and not minimized, or when in focus mode
  const { isOpen: isAppPanelOpen, isMinimized: isAppPanelMinimized, isFocusMode: isAppPanelFocusMode } = useAppPanelStore();
  const { restoredPanel: urlPanel, isFocusMode: isUrlPanelFocusMode } = useMinimizedUrlPanelsStore();

  // Check if Weekly Board URL panel is expanded (not visually minimized)
  const isUrlPanelExpanded = urlPanel && !urlPanel.isVisuallyMinimized;

  // Don't render while loading
  if (isLoading) {
    return null;
  }

  // Hide stickies when:
  // - App panel is expanded (not minimized) or in focus mode
  // - Weekly Board URL panel is expanded (not visually minimized) or in focus mode
  if (
    (isAppPanelOpen && !isAppPanelMinimized) ||
    isAppPanelFocusMode ||
    isUrlPanelExpanded ||
    isUrlPanelFocusMode
  ) {
    return null;
  }

  return (
    <Portal>
      {/* Expanded Panels */}
      <AnimatePresence mode="popLayout">
        {!goalsPanel.isCollapsed && (
          <StickyNotePanel
            key="goals-panel"
            config={panelConfigs.goals}
            content={goalsContent}
            position={goalsPanel.position}
            size={goalsPanel.size}
            isFocused={focusedPanel === 'goals'}
            onFocus={() => setFocusedPanel('goals')}
            onContentChange={handleGoalsContentChange}
            onPositionChange={handleGoalsPositionChange}
            onSizeChange={handleGoalsSizeChange}
            onMinimize={handleGoalsMinimize}
          />
        )}
        {!focusPanel.isCollapsed && (
          <StickyNotePanel
            key="focus-panel"
            config={panelConfigs.focus}
            content={focusContent}
            position={focusPanel.position}
            size={focusPanel.size}
            isFocused={focusedPanel === 'focus'}
            onFocus={() => setFocusedPanel('focus')}
            onContentChange={handleFocusContentChange}
            onPositionChange={handleFocusPositionChange}
            onSizeChange={handleFocusSizeChange}
            onMinimize={handleFocusMinimize}
          />
        )}
      </AnimatePresence>

      {/* Minimized Pill Dock */}
      <MinimizedPillDock
        collapsedPanels={collapsedPanels}
        onRestore={handleRestore}
      />
    </Portal>
  );
};
